/**
 * DocumentUpload Component
 * 
 * Provides an interface for coaches to upload documents to their team's knowledge base.
 * Features:
 * - Drag-and-drop file upload
 * - Team selection
 * - File type validation
 * - Upload progress tracking
 * - Document list with status
 * - Delete functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/config/api';

interface Document {
  id: string;
  title: string;
  file_type: string;
  file_size: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  error_message: string | null;
  team: number;
  team_name: string;
  uploaded_by: number;
  uploaded_by_name: string;
  file_url: string;
  chunk_count: number;
  created_at: string;
  updated_at: string;
}

interface Team {
  id: number;
  name: string;
}

interface DocumentUploadProps {
  teams: Team[];
  selectedTeam?: number;
  onTeamChange?: (teamId: number) => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ 
  teams, 
  selectedTeam,
  onTeamChange 
}) => {
  const [teamId, setTeamId] = useState<number | null>(selectedTeam || null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch documents when team changes
  useEffect(() => {
    if (teamId) {
      fetchDocuments();
    }
  }, [teamId]);

  // Auto-refresh documents every 5 seconds to update status
  useEffect(() => {
    if (!teamId) return;

    const interval = setInterval(() => {
      fetchDocuments();
    }, 5000);

    return () => clearInterval(interval);
  }, [teamId]);

  const fetchDocuments = async () => {
    if (!teamId) return;

    try {
      const token = localStorage.getItem('access');
      const url = new URL(getApiUrl('/api/ai/documents/'));
      url.searchParams.append('team_id', teamId.toString());

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data.documents);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
    }
  };

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTeamId = parseInt(e.target.value);
    setTeamId(newTeamId);
    if (onTeamChange) {
      onTeamChange(newTeamId);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files: FileList) => {
    if (!teamId) {
      setError('Please select a team first');
      return;
    }

    const file = files[0];

    // Validate file type
    const allowedTypes = ['pdf', 'docx', 'txt', 'md', 'rtf', 'odt'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      setError(`Unsupported file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    // Validate file size (10MB)
    const maxSize = 30 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 30MB limit');
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('team_id', teamId!.toString());
    formData.append('title', file.name);

    try {
      const token = localStorage.getItem('access');
      const response = await fetch(getApiUrl('/api/ai/documents/upload/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser sets it automatically for FormData with boundary
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      setSuccess(`Document "${file.name}" uploaded successfully!`);
      fetchDocuments();
    } catch (err: any) {
      const errorMsg = err.message || 'Upload failed';
      setError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId: string, title: string) => {
    if (!confirm(`Delete document "${title}"?`)) return;

    try {
      const token = localStorage.getItem('access');
      const response = await fetch(getApiUrl(`/api/ai/documents/${docId}/`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      setSuccess(`Document "${title}" deleted`);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      ready: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${styles[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Knowledge Base Documents
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload coaching documents to enhance AI responses with your team's knowledge.
        </p>
      </div>

      {/* Team Selection */}
      <div>
        <label htmlFor="team" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Team
        </label>
        <select
          id="team"
          value={teamId || ''}
          onChange={handleTeamChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          <option value="">Select a team</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      </div>

      {teamId && (
        <>
          {/* Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              onChange={handleFileInput}
              accept=".pdf,.docx,.txt,.md,.rtf,.odt"
              disabled={uploading}
            />

            {uploading ? (
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Uploading document...
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse w-full" />
                </div>
              </div>
            ) : (
              <>
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="mt-4">
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                  >
                    Upload a file
                  </label>
                  <span className="text-gray-500 dark:text-gray-400"> or drag and drop</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  PDF, DOCX, TXT, MD, RTF, ODT up to 10MB
                </p>
              </>
            )}
          </div>

          {/* Status Messages */}
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
              <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
            </div>
          )}

          {/* Documents List */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">
              Uploaded Documents ({documents.length})
            </h3>

            {documents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No documents uploaded yet. Upload your first document to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {doc.title}
                        </p>
                        {getStatusBadge(doc.status)}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span>{doc.file_type.toUpperCase()}</span>
                        <span>•</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        {doc.status === 'ready' && (
                          <>
                            <span>•</span>
                            <span>{doc.chunk_count} chunks</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{formatDate(doc.created_at)}</span>
                      </div>
                      {doc.error_message && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          Error: {doc.error_message}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => deleteDocument(doc.id, doc.title)}
                      className="ml-4 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete document"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentUpload;

